import { homeRedesignEn, homeRedesignEs } from './locales/homeRedesign';
import { partnersEn, partnersEs } from './locales/partners';

export const translations = {
  en: {
    // Language Switcher
    languageSwitcher: {
      en: "EN",
      es: "ES",
      switchToEnglish: "Switch to English",
      switchToSpanish: "Switch to Spanish"
    },

    header: {
      citiesLink: "Discover"
    },

    // Hero Component
    hero: {
      logoAlt: "Chisme Logo",
      floatingCta: "Find your first Chisme",
      title: "Make plans and meet women like you",
      subtitle: "Chisme is a women-first space to meet, chat, and make new friends offline. Safely and easily.",
      cta: "Find your first Chisme",
      headerImageAlt: "Chisme app mockups showing how the app works"
    },

    // Countdown Timer
    countdown: {
      launching: "We're launching in",
      launched: "We're live, bestie! 💖",
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      seconds: "Seconds"
    },

    // Stats Component
    stats: {
      items: [
        { value: 1, suffix: "", label: "City" },
        { value: 500, suffix: "+", label: "Users" },
        { value: 30, suffix: "+", label: "Plans created" },
        { value: 28, suffix: "", label: "Days online" }
      ]
    },

    // Testimonials Component
    testimonials: {
      heading: "What our community says",
      ctaCard: {
        title: "Share your story",
        description: "Have you made new friends on Chisme? We'd love to hear your experience!",
        button: "Send your review"
      },
      items: [
        {
          name: "Marta",
          age: "24 y.o.",
          text: "I don't know how, but in a few days I found a little group of girls I talk to every day. I was missing something like this. It's a safe, calm place and it's cheered me up so much.",
          rating: 4.8
        },
        {
          name: "Lucía",
          age: "31 y.o.",
          text: "I didn't expect to find my best friend here. The app is super chill, no pressure and no weird messages. Just normal girls who want to chat.",
          rating: 5.0
        },
        {
          name: "Ana",
          age: "27 y.o.",
          text: "I joined out of curiosity and ended up with a mini community I talk to about everything. This was exactly what many of us were missing. Here I can be myself.",
          rating: 4.6
        },
        {
          name: "Elena",
          age: "22 y.o.",
          text: "What I value most is how safe I feel. You can tell it's just for women. I met two girls and now we meet almost every week for coffee.",
          rating: 4.9
        },
        {
          name: "Patricia",
          age: "39 y.o.",
          text: "After work I'm usually tired and don't feel like meeting new people, but here it's super easy. No stress, just calm conversations and new friends in the end.",
          rating: 4.8
        }
      ]
    },

    // How It Works Component
    howItWorks: {
      heading: "How it works",
      steps: [
        {
          title: "Create or discover a Chisme",
          description: "Start by creating a plan you'd love, from coffee to a yoga class, or explore the map to see if something nearby catches your eye.",
          imageAlt: "Create or discover a Chisme step showing map interface"
        },
        {
          title: "Join and chat",
          description: "Join, say hi in the chat, and get a feel for the vibe before meeting up.",
          imageAlt: "Join and chat step showing group chat interface"
        },
        {
          title: "Make real friends",
          description: "Meet in person, share stories, and maybe leave with a new favorite friend (or two).",
          imageAlt: "Make real friends step showing friendship connections"
        }
      ]
    },

    // Download CTA Component
    downloadCTA: {
      title: "Download Chisme",
      subtitle: "Start creating plans, chatting, and meeting new friends from the app."
    },

    // Email Signup Component
    emailSignup: {
      earlyAccess: "EARLY ACCESS ✨",
      titleDefault: "Be the first to join Chisme ✨",
      titleSuccess: "Thanks for joining chismosa!!",
      subtitle: "We're launching soon. Sign up now and get early access when we go live!",
      emailPlaceholder: "Your email for early access",
      submitButton: "Save My Spot",
      checkboxText: "I agree to receive emails from Chisme and accept the data privacy statement. You may unsubscribe at any time using the link in any of our emails.",
      noSpam: "No spam, pinky promise 💕",
      errors: {
        recaptchaFailed: "reCAPTCHA failed. Please try again.",
        recaptchaNotLoaded: "reCAPTCHA not loaded. Please try again later.",
        submissionFailed: "Submission failed."
      }
    },

    // Footer Component
    footer: {
      socialLinks: {
        instagram: "Instagram",
        tiktok: "TikTok"
      },
      madeWith: "Made with 💜 for women everywhere"
    },

    // Terms of Service
    terms: {
      backHome: "← Back to Home",
      title: "Terms & Conditions",
      seoTitle: "Terms & Conditions | Chisme",
      seoDescription:
        "Read the terms and conditions for using the Chisme friendship app for women.",
      lastUpdated: "Last Updated:",
      lastUpdatedDate: "06.07.2026",
      links: {
        privacy: "Privacy Policy",
        guidelines: "Community Guidelines",
        safety: "Safety Tips"
      },
      sections: [
        {
          heading: "1. Introduction and Agreement",
          paragraphs: [
            'These Terms & Conditions ("Terms") govern your access to and use of the Chisme mobile application, website, and related services (together, the "Services"). Chisme is operated by "Viste Ltd", registered in the United Kingdom under company number "13232923", with its registered office at "3-7, Temple Avenue, Suite 259 Temple Chambers, London, England, EC4Y 0DA".',
            "By creating an account, downloading the App, joining a plan, buying a subscription, or otherwise using the Services, you agree to these Terms. If you do not agree, you must not use Chisme.",
            "Questions about these Terms can be sent to {{legal}}."
          ]
        },
        {
          heading: "2. Definitions",
          items: [
            '"App" means the Chisme mobile application and related web services.',
            '"Company", "we", "us", or "our" means Viste Ltd.',
            '"User", "you", or "your" means any person who registers for or uses Chisme.',
            '"Content" means text, photos, profile information, messages, plans, comments, and other material submitted to or displayed through the Services.',
            '"Premium Features" means paid features available through subscriptions, credits, trials, or other purchases.'
          ]
        },
        {
          heading: "3. Eligibility and Women-Centered Community",
          paragraphs: [
            "You must be at least 18 years old to use Chisme. You must provide truthful information and must be legally able to enter into these Terms.",
            "Chisme is designed as a women-centered community for meeting, chatting, and making plans offline. By registering, you confirm that you identify as a woman or feel comfortable participating respectfully in a women-focused environment.",
            "We may accept, decline, suspend, limit, or remove accounts where we believe this is needed to protect community safety, trust, or the integrity of the Services. Misrepresenting your identity, age, intentions, or relationship to the community may result in immediate account action."
          ]
        },
        {
          heading: "4. Account Registration and Security",
          paragraphs: [
            "You agree to keep your account information accurate, complete, and up to date. You are responsible for your login credentials and for activity that occurs through your account.",
            "You may not create accounts using bots, scripts, false identities, or another person's information. Unless we approve otherwise, you may not maintain multiple personal accounts or transfer your account to another person.",
            "If you believe your account has been accessed without permission, contact us promptly at {{legal}}."
          ]
        },
        {
          heading: "5. Verification, Location, and Safety Features",
          paragraphs: [
            "Chisme may use account verification steps, including verification photos, selfies, short liveness videos or frames, liveness-check actions, and automated service providers, to support authenticity, safety, fraud prevention, and moderation. Verification media, face-comparison processing, liveness-check processing, retention, and related service-provider processing are handled as described in our {{privacy}}.",
            "Some features use approximate location or location preferences to show nearby users, plans, or venues. You can manage location permissions through your device settings, but some features may not work without them.",
            "Verification, reporting, moderation, and location features help improve safety, but they do not guarantee that every user, plan, venue, or interaction is safe, suitable, accurate, lawful, available, or free from risk."
          ]
        },
        {
          heading: "6. Community Rules",
          paragraphs: [
            "You agree to follow these Terms and our {{guidelines}}. Chisme is built for respectful, consent-based interaction and real-life community, not harassment, spam, or unsafe behavior."
          ],
          subsections: [
            {
              heading: "You agree to:",
              items: [
                "Treat other users with kindness, respect, and honesty.",
                "Respect privacy, consent, boundaries, and personal safety.",
                "Use group chats and plans for genuine community purposes.",
                "Report inappropriate conduct or content using in-app tools or by contacting us."
              ]
            },
            {
              heading: "You must not:",
              items: [
                "Harass, threaten, stalk, intimidate, shame, exploit, or abuse another person.",
                "Post hate speech, sexual exploitation, graphic violence, illegal content, deceptive content, or content that infringes someone else's rights.",
                "Share another person's private information, photos, chats, location, or identity without consent.",
                "Impersonate someone, misrepresent your age, identity, affiliation, or intentions, or create fake or duplicate accounts.",
                "Use Chisme for scams, spam, unauthorized promotions, scraping, data mining, automated registrations, or attempts to bypass our systems.",
                "Use the Services to arrange illegal activity or conduct that creates a foreseeable risk of harm."
              ]
            }
          ]
        },
        {
          heading: "7. Plans, Chats, and In-Person Interactions",
          paragraphs: [
            "Chisme helps users discover plans, chat, and decide whether to meet in person. Unless we state otherwise in writing, Chisme is not the organizer, host, employer, agent, insurer, transportation provider, security provider, payment intermediary, venue operator, or seller for user-created plans or in-person meetings.",
            "You voluntarily choose whether to join a plan or meet another user and assume the risks associated with online and offline interactions, travel, venues, food or drink, alcohol, third-party services, weather, property loss, personal injury, illness, and other users' conduct. You are responsible for your own choices, safety, transportation, health, belongings, and compliance with laws and venue rules.",
            "We do not conduct criminal background checks on users or venues unless expressly stated. We do not guarantee any user's conduct, compatibility, identity, availability, or intentions."
          ]
        },
        {
          heading: "8. Reporting, Blocking, Moderation, and Account Action",
          paragraphs: [
            "Users can report content or conduct and block other users through the App where available. You may also contact {{legal}}.",
            "We may, but are not obligated to, monitor, screen, review, remove, restrict, demote, or preserve Content; limit, suspend, or terminate accounts; restrict access to chats, plans, or Premium Features; or notify authorities where we believe it is appropriate or legally required.",
            "Chisme reports and support channels are not emergency services and are not monitored for emergency response. If there is immediate danger or an emergency, contact local emergency services or law enforcement first.",
            "We may consider conduct that happens outside the App when it relates to Chisme users, plans, chats, events, safety, fraud, or community trust. If you believe account action was taken in error, you may contact us and request a review."
          ]
        },
        {
          heading: "9. User Content and Licence to Chisme",
          paragraphs: [
            "You keep ownership of Content you submit, subject to any rights you grant us and any rights held by others. You are responsible for your Content and must have the rights and permissions needed to share it.",
            "By submitting Content, you grant Viste Ltd a worldwide, non-exclusive, royalty-free, sublicensable, and transferable licence to host, store, reproduce, display, adapt, moderate, translate, distribute, and otherwise use that Content as needed to operate, provide, improve, promote, and protect the Services.",
            "You understand that some Content, such as profile details, plans, chats, photos, and comments, may be visible to other users depending on the feature and your settings."
          ]
        },
        {
          heading: "10. Chisme Content, Feedback, and Intellectual Property",
          paragraphs: [
            "The App, website, software, designs, trademarks, logos, copy, graphics, interfaces, and other Chisme materials are owned by Viste Ltd or its licensors and are protected by intellectual property laws.",
            "We grant you a revocable, limited, non-exclusive, non-transferable licence to install and use the App for your personal, non-commercial use, subject to these Terms.",
            "You may not copy, modify, reverse-engineer, decompile, sell, rent, sublicense, scrape, or misuse the Services except where the law allows. If you send us ideas, suggestions, or feedback, we may use them without restriction or compensation."
          ]
        },
        {
          heading: "11. Premium Features, Subscriptions, Trials, and Payments",
          paragraphs: [
            "Premium Features may be offered through weekly, monthly, yearly, introductory, trial, credit, or subscription plans. Prices, features, availability, and trial terms may vary by market, platform, account, or time.",
            "Payments for mobile subscriptions are processed by Apple App Store, Google Play, RevenueCat, or other payment providers. Their terms, billing rules, cancellation processes, taxes, and refund policies may apply.",
            "Subscriptions renew automatically unless cancelled before the renewal deadline through the relevant app store or payment provider. Deleting the App or your account does not automatically cancel a subscription.",
            "Except where required by applicable law or store rules, purchases, credits, and subscription fees are non-refundable and do not guarantee specific matches, friendships, plans, attendance, or outcomes."
          ]
        },
        {
          heading: "12. Notifications and Communications",
          paragraphs: [
            "We may send service messages, safety notices, account updates, plan reminders, chat notifications, billing messages, and legal notices through the App, email, SMS, push notifications, or other available channels.",
            "You can manage certain notification or marketing preferences in your device, email, or account settings. Some service, safety, billing, or legal communications may still be sent where permitted by law."
          ]
        },
        {
          heading: "13. Privacy and Data Protection",
          paragraphs: [
            "We process personal data in accordance with our {{privacy}}. Chisme complies with UK GDPR and EU GDPR where applicable to users in Spain, the EEA, and the United Kingdom.",
            "Our Privacy Policy explains how we use account data, profile data, messages and plans, verification media, liveness-check data, location data, app analytics, subscription events, attribution data, and service providers such as AWS Rekognition, RevenueCat, and Meta/Facebook App Events."
          ]
        },
        {
          heading: "14. Service Availability and Changes",
          paragraphs: [
            'The Services are provided "as is" and "as available." We do not guarantee that the App, website, chats, plans, subscriptions, notifications, or any feature will be uninterrupted, error-free, secure, accurate, or available on every device.',
            "We may test, modify, suspend, discontinue, restrict, or remove features, plans, offers, content, or access at any time. We may also perform maintenance or updates that temporarily affect availability."
          ]
        },
        {
          heading: "15. Disclaimers and Limitation of Liability",
          paragraphs: [
            "To the fullest extent permitted by law, Viste Ltd is not liable for user conduct, third-party services, cancelled or unavailable plans, venue issues, transportation issues, payment disputes between users, inaccurate plan information, lost opportunities, indirect losses, loss of profits, loss of data, emotional distress, personal dissatisfaction, reputational harm, or consequential damages.",
            "Safety guidance, verification features, moderation tools, reporting tools, recommendations, and community rules are provided to support the Services, but they do not create any guarantee, warranty, duty to monitor, duty to rescue, duty to investigate, fiduciary duty, or special relationship between you and Chisme.",
            "Nothing in these Terms excludes or limits liability that cannot be excluded under applicable law, including liability for death or personal injury caused by negligence, fraud, fraudulent misrepresentation, or mandatory consumer rights.",
            "If we are liable to you for any reason, our total liability will be limited to the greater of the amount you paid to Chisme in the 12 months before the event giving rise to the claim or GBP 100, unless applicable law requires otherwise."
          ]
        },
        {
          heading: "16. Indemnity",
          paragraphs: [
            "You agree to indemnify and hold Viste Ltd, its affiliates, successors, assigns, directors, officers, employees, contractors, agents, licensors, service providers, and payment providers harmless from claims, losses, liabilities, damages, costs, and expenses arising from your Content, your use or misuse of the Services, your participation in plans or in-person interactions, your breach of these Terms, your infringement of another person's rights, your dispute with another user or third party, or your unlawful conduct, except where prohibited by applicable law."
          ]
        },
        {
          heading: "17. Termination",
          paragraphs: [
            "You may stop using Chisme or delete your account at any time. Account deletion does not automatically cancel app-store subscriptions.",
            "We may suspend, limit, or terminate access to the Services at any time where we believe you have violated these Terms, created risk for other users, misused the Services, failed verification, infringed rights, or caused legal or operational risk.",
            "After termination, your right to use the Services ends immediately. Provisions that by their nature should survive termination, including ownership, licences, disclaimers, liability limits, indemnity, and dispute provisions, will continue to apply."
          ]
        },
        {
          heading: "18. United States Arbitration Agreement and Class Action Waiver",
          paragraphs: [
            "This section applies only to users located in the United States, except where prohibited by applicable law. You and Viste Ltd agree that any dispute, claim, or controversy arising out of or relating to the Services or these Terms will be resolved by binding individual arbitration under the Federal Arbitration Act, rather than in court, except that either party may bring an individual claim in small claims court where permitted or seek injunctive or equitable relief for intellectual property misuse or unauthorized access.",
            "You and Viste Ltd waive the right to a jury trial and agree that disputes must be brought only in an individual capacity, not as a plaintiff, claimant, or class member in any class, collective, consolidated, private attorney general, or representative proceeding. The arbitrator may award relief only to the individual party seeking relief and only to the extent necessary to resolve that party's individual claim.",
            "Before starting arbitration, the party bringing a claim must send written notice describing the dispute and requested relief to legal@chisme.social. The parties will try to resolve the dispute informally for 30 days. If the dispute is not resolved, either party may start arbitration with a mutually agreed arbitration provider or, if the parties cannot agree, a provider selected by a court with jurisdiction.",
            "If any part of this arbitration agreement or class action waiver is found unenforceable as to a particular claim or remedy, that part will be severed only as needed, and the rest will remain in effect to the fullest extent permitted by law."
          ]
        },
        {
          heading: "19. Governing Law and Dispute Resolution",
          paragraphs: [
            "These Terms are governed by the laws of England and Wales. Courts in England and Wales will have jurisdiction, except where mandatory consumer laws give you the right to bring claims in your country of residence.",
            "Users in the EU, UK, and other jurisdictions may benefit from mandatory consumer protections of their country or state of residence. Nothing in these Terms limits rights that cannot legally be limited. Disputes may be resolved through the EU Online Dispute Resolution platform where applicable: {{odr}}."
          ]
        },
        {
          heading: "20. Changes, Entire Agreement, and Contact",
          paragraphs: [
            "We may update these Terms from time to time. We will update the Last Updated date and, where appropriate, notify users through the App, website, email, or other reasonable means. Continued use of the Services after an update means you accept the updated Terms.",
            "These Terms, together with the {{privacy}}, {{guidelines}}, {{safety}}, and any feature-specific terms shown to you, form the entire agreement between you and Viste Ltd regarding the Services.",
            "If any part of these Terms is found invalid or unenforceable, the remaining parts will remain in effect. Our failure to enforce a term is not a waiver of our right to enforce it later. Contact: {{legal}}."
          ]
        }
      ]
    },

    // Privacy Policy
    privacy: {
      backHome: "← Back to Home",
      title: "Privacy Policy",
      seoTitle: "Privacy Policy | Chisme",
      seoDescription:
        "Learn how Chisme collects, uses, and protects your personal data.",
      lastUpdated: "Last Updated:",
      lastUpdatedDate: "06.07.2026",
      section1: {
        heading: "1. Introduction",
        para1: 'Chisme ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, share, and protect your information when you use the Chisme mobile application (the "App") and related services.',
        para2: "By using Chisme, you acknowledge this Privacy Policy. Where consent is required by law, such as for optional location access, marketing, advertiser tracking identifiers, or face verification, we rely on the affirmative action you take in the relevant flow or request consent separately."
      },
      section2: {
        heading: "2. Data Controller",
        para1: 'The data controller is "Viste Ltd", registered in the United Kingdom under company number "13232923", with registered office at 3-7, Temple Avenue, Suite 259 Temple Chambers, London, England, EC4Y 0DA.',
        contact: "Contact: ",
        para2: "For EU/EEA users (including Spain), we comply with EU GDPR and, if required, will appoint an EU representative."
      },
      section3: {
        heading: "3. Information We Collect",
        subsectionA: {
          heading: "(a) Information you provide",
          items: [
            "Registration details (name, email, date of birth, gender identity).",
            "Profile details (photos, interests, location preferences).",
            "Content you share (messages, events, posts)."
          ]
        },
        subsectionB: {
          heading: "(b) Information we automatically collect",
          items: [
            "Device data (model, operating system, unique identifiers).",
            "Usage data (interactions in the App, features used).",
            "Approximate location (if enabled by you for event discovery)."
          ]
        },
        subsectionC: {
          heading: "(c) Information from third parties",
          items: [
            "Payments data from App Store / Google Play.",
            "Social media accounts (if you choose to link them)."
          ]
        },
        subsectionD: {
          heading: "(d) Verification Photos, Videos, and Liveness Checks",
          para1: 'To help ensure the safety and authenticity of our community, we require users to complete account verification, which may include submitting a selfie or photo, recording a short liveness video, completing simple liveness-check actions, or allowing still frames to be extracted from verification video (together, "Verification Media"). Verification Media may include the user\'s face and may be compared against the user\'s own profile photos using AWS Rekognition, a service provided by Amazon Web Services, Inc.',
          para2: "This one-to-one comparison and liveness-check process may involve processing facial images, face-comparison information, liveness-check actions, and related verification results, which may be considered biometric data or biometric identifiers under certain laws."
        },
        note: "We do not knowingly collect data from individuals under 18 years old."
      },
      section4: {
        heading: "4. How We Use Your Information",
        items: [
          "To provide and operate the App.",
          "To create and manage your account.",
          "To connect you with other users and events.",
          "To ensure safety and compliance with our women-centered policy.",
          "To process subscriptions and payments.",
          "To send updates, notifications, and customer support responses.",
          "To improve our services and personalize your experience.",
          "To comply with legal obligations.",
          "To verify that users are real people and to prevent fake or fraudulent accounts.",
          "To analyze uploaded photos and images with AWS Rekognition for safety and policy-enforcement purposes, including detecting explicit, violent, or otherwise prohibited content.",
          "To measure the effectiveness of our marketing campaigns and understand subscription conversions.",
          "To attribute app installs, app opens, trials, and subscription events to advertising campaigns, including through privacy-preserving and aggregated measurement tools."
        ]
      },
      section4a: {
        heading: "4a. Location Data",
        para1: "With your consent, Chisme accesses your device's location when you open the app. This information is used only to:",
        items: [
          "Show your position on the map within the App.",
          "Display users and plans located near you."
        ],
        para2: "Your location is refreshed only when you open the app and is not tracked in real time. We do not share your location data with third parties. You can withdraw consent at any time by disabling location permissions in your device settings, but certain features may not work properly without it."
      },
      section4b: {
        heading: "4b. Verification Media, Liveness Checks, and Face Data",
        para1: "Verification Media is used solely to verify that an account belongs to a real person, complete liveness checks, and support safety, trust, and fraud prevention on the platform. When you choose to start verification and submit Verification Media, your facial image, face-comparison data, liveness-check actions, and related verification results may be collected and processed for identity verification and platform safety as described in this Policy.",
        notUsedForIntro: "We do not use Verification Media for:",
        notUsedForItems: [
          "Searching your face across other users, building a searchable face database, or real-world identity lookups",
          "Selling, leasing, trading, or otherwise profiting from biometric data or biometric identifiers",
          "Advertising, profiling, or tracking",
          "Training artificial intelligence or machine learning models"
        ],
        para2: "Verification Media, profile photos used for comparison, liveness-check data, and any face-comparison results or data are accessed only for verification, moderation, and safety purposes. Verification Media, liveness-check data, and face-comparison data are deleted within 7 days after verification is completed, unless we must retain them longer to comply with law or investigate fraud or security incidents."
      },
      section5: {
        heading: "5. Legal Basis (GDPR / UK GDPR)",
        items: [
          "Contract – to provide the services you request.",
          "Consent – for optional features such as marketing emails or location data.",
          "Consent or another applicable lawful basis – for face verification and liveness checks involving facial images, face-comparison data, liveness-check data, or related verification results that may be considered biometric data under applicable laws.",
          "Consent – for advertiser tracking identifiers and other optional tracking where required by law or platform rules.",
          "Legal obligation – for compliance with applicable law (tax, consumer, security).",
          "Legitimate interests – to ensure safety, prevent fraud, moderate uploaded photos and other content, and improve services.",
          "Legitimate interests – to measure app performance, understand subscription conversions, and improve our marketing, unless consent is required."
        ]
      },
      section6: {
        heading: "6. Sharing Your Information",
        intro: "We may share your data with:",
        items: [
          "Service providers (hosting, analytics, payment processors).",
          "Legal authorities if required by law.",
          "Other users, but only with the data you choose to share in your profile."
        ],
        attributionParas: [
          "We use third-party analytics, subscription management, and advertising attribution providers, including RevenueCat and Meta/Facebook App Events, to operate subscriptions, understand app performance, and measure the effectiveness of our advertising campaigns.",
          "When you interact with the App or purchase a subscription, we may share limited app event data such as app opens, trial starts, subscription purchases, product identifiers, purchase value, currency, device information, and account identifiers with these providers. Where supported, certain contact information such as email address or name may be hashed or otherwise processed before being used for matching or attribution.",
          "We use AWS Rekognition (Amazon Web Services, Inc.) as a service provider/processor for AI-powered image moderation and identity verification. Uploaded photos and images may be automatically analyzed to detect explicit, violent, or otherwise prohibited content, and flagged images may be reviewed by authorized human moderators under confidentiality obligations.",
          "On iOS, we request App Tracking Transparency permission before accessing advertiser tracking identifiers such as IDFA. If you deny tracking permission, we do not use IDFA for advertising tracking, but we may still send app events for aggregated measurement where permitted."
        ],
        note: "We do not sell your personal data.",
        verificationNote: "Verification Media, liveness-check data, and face-comparison data are not shared with third parties for marketing or advertising. AWS Rekognition processes this data only as our service provider/processor for verification and image moderation, and access is limited to those purposes and subject to contractual confidentiality and data-protection obligations."
      },
      section7: {
        heading: "7. International Transfers",
        text: "Your data may be transferred outside the UK and EU/EEA. Where this happens, we use safeguards such as Standard Contractual Clauses (SCCs) to ensure adequate protection."
      },
      section8: {
        heading: "8. Data Retention",
        items: [
          "We keep your data as long as you have an account.",
          "If you delete your account, we erase or anonymize your data within 30 days, unless we must keep some records for legal obligations (e.g., tax records).",
          "We may preserve account data, reports, messages, content, device information, logs, and other records where reasonably necessary for safety, fraud prevention, dispute resolution, legal claims, law enforcement requests, enforcement of our Terms, or protection of users and the Services."
        ],
        verificationNote: "Verification Media, liveness-check data, and face-comparison results or data are deleted within 7 days after verification is completed, unless retention is required to comply with law or to investigate fraud or security incidents. Where possible, we minimize or delete underlying image or video content when it is no longer needed for safety or legal purposes."
      },
      section9: {
        heading: "9. Your Rights (GDPR / UK GDPR)",
        intro: "You have the right to:",
        items: [
          "Access your data.",
          "Rectify incorrect data.",
          'Request deletion ("right to be forgotten").',
          "Restrict processing.",
          "Object to processing.",
          "Data portability.",
          "Withdraw consent at any time, including consent for face verification where consent is the applicable lawful basis, by changing your account settings where available or contacting us.",
          "File a complaint with your local data authority (ICO in the UK, AEPD in Spain)."
        ],
        requestsText: "Requests can be made at "
      },
      section10: {
        heading: "10. Security",
        text: "We use industry-standard measures (encryption, firewalls, access controls) to protect your data. However, no system is 100% secure. Users are responsible for protecting their login details.",
        verificationNote: "Verification Media, facial images, liveness-check data, and face-comparison data are protected using the same industry-standard security measures applied to other sensitive personal data, and access is restricted to authorized personnel and service providers who need it for verification, moderation, safety, legal, or security purposes."
      },
      section11: {
        heading: "11. Children's Privacy",
        text: "The App is not intended for users under 18 years old. We will delete any account suspected of underage use."
      },
      section12: {
        heading: "12. Changes to This Policy",
        text: 'We may update this Privacy Policy. Updates will be posted in the App with a new "Last Updated" date.'
      },
      section13: {
        heading: "13. Contact Us",
        intro: "If you have questions or concerns:",
        email: "Email: ",
        address: "Address: 3-7, Temple Avenue, Suite 259 Temple Chambers, London, England, EC4Y 0DA"
      }
    },

    // Community Guidelines
    guidelines: {
      backHome: "← Back to Home",
      title: "Community Guidelines",
      seoTitle: "Community Guidelines | Chisme",
      seoDescription:
        "Community rules for staying safe and respectful on the Chisme friendship app.",
      lastUpdated: "Last Updated:",
      lastUpdatedDate: "06.07.2026",
      intro: "Chisme is a women-centered community for making friends, chatting, and joining real-life plans in a safe and respectful environment. These Community Guidelines are part of our Terms and apply in the App, in chats, on profiles, in plans, and during in-person meetups. If we find that you violated these Guidelines, we may remove content, warn you, restrict features, suspend your account, or permanently remove you from Chisme, depending on the severity and history of the conduct.",
      section1: {
        heading: "1. Community Norms",
        items: [
          "Chisme is not a dating app, hookup app, or platform for sexual solicitation. Inappropriate sexual messaging, pressure, harassment, or misuse of messaging features is not allowed.",
          "Treat every member with kindness, respect, and honesty. Harassment, bullying, intimidation, threats, shaming, or degrading language are prohibited.",
          "Be authentic. Do not create fake profiles, impersonate others, misrepresent your age, gender identity, location, relationship to the community, or intentions.",
          "Use Chisme for genuine friendship, group plans, and community. Repeated low-effort, irrelevant, or mass unsolicited messages may lead to restrictions or removal."
        ]
      },
      section2: {
        heading: "2. Prohibited Content and Conduct",
        items: [
          "No hate speech, discrimination, or dehumanizing content based on race, ethnicity, nationality, gender, sexual orientation, religion, disability, age, or any protected characteristic.",
          "No violence, threats, stalking, coercion, physical misconduct, sexual misconduct, or content that promotes self-harm, violence, illegal activity, or dangerous behavior.",
          "Do not share explicit, vulgar, sexually provocative, exploitative, or unsolicited sexual content. Plans must not involve sex work, pornography, or their promotion.",
          "Do not use Chisme for fraud, scams, requests for money, deceptive financial gain, spam, misleading links, unauthorized promotions, scraping, bots, ban evasion, or attempts to bypass our systems.",
          "Users must be at least 18. Do not use Chisme to share photos or information about minors or to arrange contact with anyone under 18."
        ]
      },
      section3: {
        heading: "3. Profiles and Photos",
        items: [
          "Your profile name should reflect your real name or a real nickname, not another person's identity or a deceptive persona.",
          "Profile photos and verification media must be of you and must not impersonate or misrepresent another person.",
          "Do not include sensitive information such as phone numbers, home addresses, financial details, email addresses, private travel details, or other personal data in your bio.",
          "Keep profile content appropriate for a friendship and community app. Explicit, misleading, hateful, commercial, or unsafe profile content may be removed."
        ]
      },
      section4: {
        heading: "4. Chats and Privacy",
        items: [
          "Protect your privacy and other people's privacy. Do not publicly share or request phone numbers, addresses, email addresses, financial information, or exact private locations.",
          "Keep discussions and plan coordination inside Chisme unless everyone involved freely chooses otherwise. Do not pressure users to move to another channel.",
          "Do not screenshot, record, publish, or share chats, photos, profiles, locations, or personal information outside Chisme without consent.",
          "Respect boundaries. If someone says no, stops replying, blocks you, or asks for space, do not continue contacting or pressuring them."
        ]
      },
      section5: {
        heading: "5. Plans and Real-Life Meetups",
        items: [
          "Plans are for Chisme users who are 18 or older and must comply with local laws, venue rules, and safety expectations, including rules about alcohol, controlled substances, and public disruption.",
          "Choose public spaces, tell a friend or family member where you are going, review plan details carefully, and trust your instincts if something feels unsafe.",
          "Creators must provide accurate and timely details such as time, location, participant limits, costs, and accessibility considerations. Participants should verify details before attending.",
          "Be reliable and respectful with attendance. If you need to cancel, tell the group promptly. Repeated last-minute cancellations or no-shows may result in account action.",
          "Participants are responsible for their own transportation, safety, health, belongings, bills, and payment arrangements. Chisme does not supervise meetups or guarantee user conduct or outcomes."
        ]
      },
      section6: {
        heading: "6. Blocking, Reporting, and Emergencies",
        items: [
          "If there is an emergency or immediate danger, contact local emergency services or law enforcement first.",
          "You can block users from their profile or conversation where available. Blocking stops contact but does not automatically report the behavior to Chisme.",
          "If someone violates these Guidelines, report the profile, content, chat, or plan through the App or contact us so our team can review it.",
          "Reports are not monitored as an emergency response channel, and Chisme does not guarantee a specific response time, outcome, investigation, or enforcement action.",
          "Misusing blocking or reporting to harass, retaliate against, silence, or inconvenience others may itself result in account action."
        ]
      },
      section7: {
        heading: "7. Enforcement and Accountability",
        items: [
          "Chisme may review reported or suspicious content, chats, profiles, plans, and behavior that affects platform safety, including conduct connected to in-person meetups.",
          "We may remove content, limit visibility, restrict messaging or plan features, require verification, suspend accounts, terminate accounts, or notify authorities when appropriate or legally required.",
          "Trying to evade enforcement by creating new accounts, using false information, using VPNs or automation, or encouraging others to violate these Guidelines is prohibited.",
          "These Guidelines may be updated as Chisme grows. Continued use of Chisme means you agree to follow the current version."
        ]
      }
    },

    // Safety
    safety: {
      backHome: "← Back to Home",
      title: "Safety Tips and Plan Etiquette",
      seoTitle: "Safety Tips | Chisme",
      seoDescription:
        "Practical safety guidance for using Chisme, joining plans, and meeting new people.",
      lastUpdated: "Last Updated:",
      lastUpdatedDate: "06.07.2026",
      intro:
        "Chisme is designed to help women make friends, chat, and join real-life plans with greater confidence. Safety is a shared responsibility. These tips do not eliminate all risk, create a guarantee of safety, or make Chisme responsible for user conduct, venues, transportation, or third-party services.",
      sections: [
        {
          heading: "1. Before Joining a Plan",
          items: [
            "Review the plan details carefully, including time, location, participant limits, expected costs, accessibility considerations, and any venue rules.",
            "Meet in a public, well-lit place where other people are present, especially when meeting someone for the first time.",
            "Tell a trusted friend or family member where you are going, who you expect to meet, and when you expect to return.",
            "Arrange your own transportation to and from the location so you can leave at any time.",
            "Keep plan-related communication in Chisme until you feel comfortable. In-app conversations provide context if you need to report a concern.",
            "Verify venue details independently when needed. Information shown in the App, including user-provided details, may change or be incomplete."
          ]
        },
        {
          heading: "2. During a Plan",
          subsections: [
            {
              heading: "Personal Safety",
              items: [
                "Stay aware of your surroundings and keep your belongings, phone, and drinks in sight.",
                "Use judgment with alcohol or any substance that may affect your ability to make decisions or leave safely.",
                "Trust your instincts. If something feels unsafe or inappropriate, leave the situation and seek help.",
                "If you feel unsafe, contact venue staff, local authorities, emergency services, or a trusted contact immediately."
              ]
            },
            {
              heading: "Etiquette and Conduct",
              items: [
                "Treat other participants with respect and keep conversations inclusive and appropriate.",
                "Respect personal boundaries, privacy, and consent at all times.",
                "Be prepared to manage your own bill, purchases, tips, and payment arrangements. Chisme does not handle payment splitting between users.",
                "Follow local laws, venue policies, and cultural expectations."
              ]
            }
          ]
        },
        {
          heading: "3. After a Plan",
          items: [
            "Continue conversations in Chisme until you are comfortable sharing additional contact details.",
            "Avoid accepting rides from people you do not know well, especially if alcohol or other substances are involved.",
            "If you experienced inappropriate, unsafe, or suspicious behavior, report it through the App as soon as possible.",
            "If you need to cancel future plans, tell the group promptly and respectfully."
          ]
        },
        {
          heading: "4. Blocking and Reporting",
          items: [
            "Use blocking to stop unwanted contact where the feature is available.",
            "Blocking does not automatically report a user to Chisme. If someone violates our Community Guidelines or creates a safety concern, submit a report through the App.",
            "Reports help us review harmful behavior, enforce our rules, and improve community safety.",
            "Do not misuse blocking or reporting to harass, retaliate against, silence, or inconvenience another user."
          ]
        },
        {
          heading: "5. Online Safety",
          items: [
            "Never send money, financial information, account credentials, identity documents, or verification codes to someone you met on Chisme.",
            "Be cautious of anyone who asks for money, investment help, donations, emergency financial support, or unusual payment arrangements.",
            "Protect sensitive information such as your home or work address, daily routine, exact live location, financial details, and private family information.",
            "Be cautious if someone tries to move the conversation away from Chisme very quickly, avoids reasonable verification, pressures you emotionally, or refuses to respect boundaries.",
            "Report suspicious behavior, including fake profiles, underage users, harassment, threats, spam, scams, and inappropriate sexual messages."
          ]
        },
        {
          heading: "6. Emergencies and Immediate Risk",
          items: [
            "If there is an emergency or immediate danger, contact local emergency services or law enforcement first.",
            "Chisme is not an emergency service, crisis hotline, security provider, medical provider, transportation provider, or law enforcement agency. Reports submitted through the App are not monitored for emergency response.",
            "If you are in Spain or elsewhere in the European Union, 112 is the general emergency number.",
            "If you are experiencing gender-based violence in Spain, 016 provides support and does not appear on phone bills, although it may appear in call logs depending on the device.",
            "After you are safe, report the incident in Chisme so our team can review account or content action where appropriate."
          ]
        },
        {
          heading: "7. Support Resources",
          items: [
            "Emergency services: contact local authorities immediately if there is immediate danger.",
            "Spain general emergencies: 112.",
            "Spain gender-based violence support: 016.",
            "European Union emergency number: 112.",
            "For non-emergency Chisme support, contact hola@chisme.social or use in-app reporting where available."
          ]
        },
        {
          heading: "8. Chisme's Role",
          items: [
            "Chisme provides tools for discovery, chat, reporting, blocking, moderation, and account action, but we do not supervise every plan or guarantee user conduct.",
            "Users are responsible for their own choices, transportation, health, safety, belongings, and payment arrangements when joining plans or meeting others.",
            "Safety features and moderation can reduce risk, but they cannot remove every risk from online or in-person interactions.",
            "Chisme may review, preserve, restrict, or remove content and accounts where appropriate, but has no obligation to monitor all users, chats, plans, venues, reports, or interactions."
          ]
        }
      ]
    },

    homeRedesign: homeRedesignEn,
    partners: partnersEn
  },

  es: {
    // Language Switcher
    languageSwitcher: {
      en: "EN",
      es: "ES",
      switchToEnglish: "Cambiar a inglés",
      switchToSpanish: "Cambiar a español"
    },

    header: {
      citiesLink: "Descubrir"
    },

    // Hero Component
    hero: {
      logoAlt: "Logo de Chisme",
      floatingCta: "Encuentra tu primer Chisme",
      title: "Haz planes y conoce mujeres como tú",
      subtitle: "Chisme es un espacio prioritario para mujeres para conocer, chatear y hacer nuevas amigas offline. De forma segura y fácil.",
      cta: "Encuentra tu primer Chisme",
      headerImageAlt: "Mockups de la app Chisme mostrando cómo funciona la aplicación"
    },

    // Countdown Timer
    countdown: {
      launching: "Lanzamos en",
      launched: "¡Ya estamos aquí, bestie! 💖",
      days: "Días",
      hours: "Horas",
      minutes: "Minutos",
      seconds: "Segundos"
    },

    // Stats Component
    stats: {
      items: [
        { value: 1, suffix: "", label: "Ciudad" },
        { value: 500, suffix: "+", label: "Usuarias" },
        { value: 30, suffix: "+", label: "Planes creados" },
        { value: 28, suffix: "", label: "Días online" }
      ]
    },

    // Testimonials Component
    testimonials: {
      heading: "Lo que dice nuestra comunidad",
      ctaCard: {
        title: "Comparte tu historia",
        description: "¿Has hecho nuevas amigas en Chisme? ¡Nos encantaría conocer tu experiencia!",
        button: "Enviar tu valoración"
      },
      items: [
        {
          name: "Marta",
          age: "24 años",
          text: "No sé cómo, pero en unos días encontré un grupito de chicas con las que hablo cada día. Me faltaba algo así. Es un sitio seguro, tranquilo y me ha animado un montón.",
          rating: 4.8
        },
        {
          name: "Lucía",
          age: "31 años",
          text: "No esperaba encontrar a mi mejor amiga aquí. La app es súper chill, sin presión y sin mensajes raros. Solo chicas normales que quieren charlar.",
          rating: 5.0
        },
        {
          name: "Ana",
          age: "27 años",
          text: "Entré por curiosidad y al final tengo una mini comunidad con la que hablo de todo. Esto era justo lo que nos faltaba a muchas. Aquí puedo ser yo misma.",
          rating: 4.6
        },
        {
          name: "Elena",
          age: "22 años",
          text: "Lo que más valoro es lo segura que me siento. Se nota que es solo para mujeres. Conocí a dos chicas y ahora quedamos casi cada semana para café.",
          rating: 4.9
        },
        {
          name: "Patricia",
          age: "39 años",
          text: "Después del trabajo suelo estar cansada y no me apetece conocer gente nueva, pero aquí es súper fácil. Sin estrés, solo conversaciones tranquilas y nuevas amigas al final.",
          rating: 4.8
        }
      ]
    },

    // How It Works Component
    howItWorks: {
      heading: "Cómo funciona",
      steps: [
        {
          title: "Crea o descubre un chisme",
          description: "Empieza creando un plan que te apetezca, desde un café hasta una clase de yoga, o explora el mapa para ver si ya hay algo cerca que te interese.",
          imageAlt: "Paso Crea o descubre un chisme mostrando la interfaz del mapa"
        },
        {
          title: "Únete y conversa",
          description: "Apúntate, saluda en el chat y siente el ambiente antes de lanzarte.",
          imageAlt: "Paso Únete y conversa mostrando la interfaz del chat grupal"
        },
        {
          title: "Haz amigas reales",
          description: "Queda en persona, compartid historias y quizá te vayas con una nueva amiga favorita (o dos).",
          imageAlt: "Paso Haz amigas reales mostrando conexiones de amistad"
        }
      ]
    },

    // Download CTA Component
    downloadCTA: {
      title: "Descarga Chisme",
      subtitle: "Empieza a crear planes, chatear y conocer nuevas amigas desde la app."
    },

    // Email Signup Component
    emailSignup: {
      earlyAccess: "ACCESO ANTICIPADO ✨",
      titleDefault: "Apúntate antes que nadie ✨",
      titleSuccess: "¡¡Gracias por unirte chismosa!!",
      subtitle: "¡Regístrate y consigue acceso anticipado en cuanto estemos en marcha!",
      emailPlaceholder: "Tu email para entrar antes",
      submitButton: "Guardar mi lugar",
      checkboxText: "Acepto recibir emails de Chisme y acepto la declaración de privacidad de datos. Puedes darte de baja en cualquier momento usando el enlace en cualquiera de nuestros emails.",
      noSpam: "Cero spam, lo prometemos 💕",
      errors: {
        recaptchaFailed: "reCAPTCHA falló. Por favor intenta de nuevo.",
        recaptchaNotLoaded: "reCAPTCHA no se cargó. Por favor intenta más tarde.",
        submissionFailed: "El envío falló."
      }
    },

    // Footer Component
    footer: {
      socialLinks: {
        instagram: "Instagram",
        tiktok: "TikTok"
      },
      madeWith: "Hecho con 💜 para mujeres en todas partes"
    },

    // Terms of Service
    terms: {
      backHome: "← Volver al inicio",
      title: "Términos y Condiciones",
      seoTitle: "Términos y Condiciones | Chisme",
      seoDescription:
        "Lee los términos y condiciones de uso de la app de amistad Chisme para mujeres.",
      lastUpdated: "Última actualización:",
      lastUpdatedDate: "06.07.2026",
      links: {
        privacy: "Política de Privacidad",
        guidelines: "Normas de la Comunidad",
        safety: "Consejos de Seguridad"
      },
      sections: [
        {
          heading: "1. Introducción y Aceptación",
          paragraphs: [
            'Estos Términos y Condiciones ("Términos") regulan tu acceso y uso de la aplicación móvil Chisme, el sitio web y los servicios relacionados (en conjunto, los "Servicios"). Chisme está operada por "Viste Ltd", registrada en el Reino Unido con número de empresa "13232923", con domicilio registrado en "3-7, Temple Avenue, Suite 259 Temple Chambers, Londres, Inglaterra, EC4Y 0DA".',
            "Al crear una cuenta, descargar la App, unirte a un plan, comprar una suscripción o utilizar los Servicios de cualquier otra forma, aceptas estos Términos. Si no estás de acuerdo, no debes usar Chisme.",
            "Puedes enviar preguntas sobre estos Términos a {{legal}}."
          ]
        },
        {
          heading: "2. Definiciones",
          items: [
            '"App" significa la aplicación móvil Chisme y los servicios web relacionados.',
            '"Compañía", "nosotras", "nos" o "nuestro" significa Viste Ltd.',
            '"Usuaria", "tú" o "tu" significa cualquier persona que se registre o use Chisme.',
            '"Contenido" significa texto, fotos, información de perfil, mensajes, planes, comentarios y otro material enviado o mostrado a través de los Servicios.',
            '"Funcionalidades Premium" significa funciones de pago disponibles mediante suscripciones, créditos, pruebas u otras compras.'
          ]
        },
        {
          heading: "3. Elegibilidad y Comunidad Centrada en Mujeres",
          paragraphs: [
            "Debes tener al menos 18 años para usar Chisme. Debes proporcionar información veraz y tener capacidad legal para aceptar estos Términos.",
            "Chisme está diseñada como una comunidad centrada en mujeres para conocer, chatear y hacer planes offline. Al registrarte, confirmas que te identificas como mujer o que te sientes cómoda participando con respeto en un entorno enfocado en mujeres.",
            "Podemos aceptar, rechazar, suspender, limitar o eliminar cuentas cuando creamos que es necesario para proteger la seguridad, confianza o integridad de los Servicios. Tergiversar tu identidad, edad, intenciones o relación con la comunidad puede resultar en medidas inmediatas sobre tu cuenta."
          ]
        },
        {
          heading: "4. Registro de Cuenta y Seguridad",
          paragraphs: [
            "Aceptas mantener la información de tu cuenta exacta, completa y actualizada. Eres responsable de tus credenciales de acceso y de la actividad que ocurra a través de tu cuenta.",
            "No puedes crear cuentas mediante bots, scripts, identidades falsas o información de otra persona. Salvo que lo aprobemos, no puedes mantener varias cuentas personales ni transferir tu cuenta a otra persona.",
            "Si crees que alguien ha accedido a tu cuenta sin permiso, contacta cuanto antes con {{legal}}."
          ]
        },
        {
          heading: "5. Verificación, Ubicación y Funciones de Seguridad",
          paragraphs: [
            "Chisme puede usar pasos de verificación de cuenta, incluidas fotos de verificación, selfies, videos cortos o fotogramas de prueba de vida, acciones de prueba de vida y proveedores de servicios automatizados, para apoyar la autenticidad, seguridad, prevención de fraude y moderación. Los medios de verificación, el procesamiento de comparación facial, el procesamiento de prueba de vida, la retención y el tratamiento relacionado por proveedores de servicios se gestionan según nuestra {{privacy}}.",
            "Algunas funciones usan ubicación aproximada o preferencias de ubicación para mostrar usuarias, planes o locales cercanos. Puedes gestionar los permisos de ubicación en la configuración de tu dispositivo, pero algunas funciones pueden no funcionar sin ellos.",
            "La verificación, los reportes, la moderación y las funciones de ubicación ayudan a mejorar la seguridad, pero no garantizan que cada usuaria, plan, local o interacción sea segura, adecuada, exacta, legal, disponible o libre de riesgo."
          ]
        },
        {
          heading: "6. Reglas de la Comunidad",
          paragraphs: [
            "Aceptas cumplir estos Términos y nuestras {{guidelines}}. Chisme está creada para interacciones respetuosas, basadas en el consentimiento y orientadas a comunidad real, no para acoso, spam o conductas inseguras."
          ],
          subsections: [
            {
              heading: "Aceptas:",
              items: [
                "Tratar a otras usuarias con amabilidad, respeto y honestidad.",
                "Respetar la privacidad, el consentimiento, los límites y la seguridad personal.",
                "Usar los chats de grupo y planes para fines genuinos de comunidad.",
                "Reportar conductas o contenidos inapropiados mediante las herramientas de la App o contactándonos."
              ]
            },
            {
              heading: "No debes:",
              items: [
                "Acosar, amenazar, perseguir, intimidar, humillar, explotar o abusar de otra persona.",
                "Publicar discurso de odio, explotación sexual, violencia gráfica, contenido ilegal, contenido engañoso o contenido que infrinja derechos de terceros.",
                "Compartir información privada, fotos, chats, ubicación o identidad de otra persona sin consentimiento.",
                "Suplantar a alguien, tergiversar tu edad, identidad, afiliación o intenciones, o crear cuentas falsas o duplicadas.",
                "Usar Chisme para estafas, spam, promociones no autorizadas, scraping, extracción de datos, registros automatizados o intentos de eludir nuestros sistemas.",
                "Usar los Servicios para organizar actividades ilegales o conductas que creen un riesgo previsible de daño."
              ]
            }
          ]
        },
        {
          heading: "7. Planes, Chats e Interacciones Presenciales",
          paragraphs: [
            "Chisme ayuda a las usuarias a descubrir planes, chatear y decidir si quieren conocerse en persona. Salvo que indiquemos lo contrario por escrito, Chisme no es organizadora, anfitriona, empleadora, agente, aseguradora, proveedora de transporte, proveedora de seguridad, intermediaria de pagos, operadora de locales ni vendedora respecto de planes creados por usuarias o encuentros presenciales.",
            "Tú eliges voluntariamente si unirte a un plan o conocer a otra usuaria y asumes los riesgos asociados a interacciones online y offline, viajes, locales, comida o bebida, alcohol, servicios de terceros, clima, pérdida de pertenencias, lesiones personales, enfermedad y conducta de otras usuarias. Eres responsable de tus propias decisiones, seguridad, transporte, salud, pertenencias y cumplimiento de leyes y normas del local.",
            "No realizamos comprobaciones de antecedentes penales de usuarias o locales salvo que se indique expresamente. No garantizamos la conducta, compatibilidad, identidad, disponibilidad o intenciones de ninguna usuaria."
          ]
        },
        {
          heading: "8. Reportes, Bloqueos, Moderación y Medidas sobre Cuentas",
          paragraphs: [
            "Las usuarias pueden reportar contenido o conducta y bloquear a otras usuarias desde la App cuando esté disponible. También puedes contactar con {{legal}}.",
            "Podemos, pero no estamos obligadas a, monitorizar, filtrar, revisar, eliminar, restringir, degradar o conservar Contenido; limitar, suspender o terminar cuentas; restringir el acceso a chats, planes o Funcionalidades Premium; o notificar a autoridades cuando lo consideremos apropiado o legalmente requerido.",
            "Los reportes y canales de soporte de Chisme no son servicios de emergencia y no se monitorizan para respuesta de emergencia. Si existe peligro inmediato o una emergencia, contacta primero con los servicios locales de emergencia o las autoridades.",
            "Podemos tener en cuenta conductas que ocurran fuera de la App cuando estén relacionadas con usuarias, planes, chats, eventos, seguridad, fraude o confianza de la comunidad de Chisme. Si crees que una medida sobre tu cuenta fue tomada por error, puedes contactarnos y solicitar una revisión."
          ]
        },
        {
          heading: "9. Contenido de Usuarias y Licencia a Chisme",
          paragraphs: [
            "Conservas la titularidad del Contenido que envías, sujeto a los derechos que nos concedes y a los derechos de terceros. Eres responsable de tu Contenido y debes tener los derechos y permisos necesarios para compartirlo.",
            "Al enviar Contenido, concedes a Viste Ltd una licencia mundial, no exclusiva, libre de regalías, sublicenciable y transferible para alojar, almacenar, reproducir, mostrar, adaptar, moderar, traducir, distribuir y usar dicho Contenido según sea necesario para operar, proporcionar, mejorar, promocionar y proteger los Servicios.",
            "Entiendes que cierto Contenido, como detalles de perfil, planes, chats, fotos y comentarios, puede ser visible para otras usuarias según la función y tus ajustes."
          ]
        },
        {
          heading: "10. Contenido de Chisme, Feedback y Propiedad Intelectual",
          paragraphs: [
            "La App, el sitio web, el software, diseños, marcas, logos, textos, gráficos, interfaces y otros materiales de Chisme pertenecen a Viste Ltd o a sus licenciantes y están protegidos por leyes de propiedad intelectual.",
            "Te concedemos una licencia revocable, limitada, no exclusiva y no transferible para instalar y usar la App para tu uso personal y no comercial, sujeto a estos Términos.",
            "No puedes copiar, modificar, realizar ingeniería inversa, descompilar, vender, alquilar, sublicenciar, hacer scraping o usar indebidamente los Servicios salvo cuando la ley lo permita. Si nos envías ideas, sugerencias o feedback, podemos usarlos sin restricción ni compensación."
          ]
        },
        {
          heading: "11. Funcionalidades Premium, Suscripciones, Pruebas y Pagos",
          paragraphs: [
            "Las Funcionalidades Premium pueden ofrecerse mediante planes semanales, mensuales, anuales, introductorios, de prueba, créditos o suscripciones. Los precios, funciones, disponibilidad y condiciones de prueba pueden variar por mercado, plataforma, cuenta o momento.",
            "Los pagos de suscripciones móviles son procesados por Apple App Store, Google Play, RevenueCat u otros proveedores de pago. Pueden aplicarse sus términos, reglas de facturación, procesos de cancelación, impuestos y políticas de reembolso.",
            "Las suscripciones se renuevan automáticamente salvo que se cancelen antes del plazo de renovación a través de la tienda de apps correspondiente o proveedor de pago. Eliminar la App o tu cuenta no cancela automáticamente una suscripción.",
            "Salvo cuando lo exija la ley aplicable o las reglas de la tienda, las compras, créditos y cuotas de suscripción no son reembolsables y no garantizan matches, amistades, planes, asistencia o resultados concretos."
          ]
        },
        {
          heading: "12. Notificaciones y Comunicaciones",
          paragraphs: [
            "Podemos enviar mensajes de servicio, avisos de seguridad, actualizaciones de cuenta, recordatorios de planes, notificaciones de chat, mensajes de facturación y avisos legales a través de la App, email, SMS, notificaciones push u otros canales disponibles.",
            "Puedes gestionar ciertas preferencias de notificaciones o marketing en tu dispositivo, email o ajustes de cuenta. Algunas comunicaciones de servicio, seguridad, facturación o legales pueden seguir enviándose cuando la ley lo permita."
          ]
        },
        {
          heading: "13. Privacidad y Protección de Datos",
          paragraphs: [
            "Tratamos los datos personales de acuerdo con nuestra {{privacy}}. Chisme cumple con el GDPR del Reino Unido y el GDPR de la UE cuando resulten aplicables a usuarias en España, el EEE y el Reino Unido.",
            "Nuestra Política de Privacidad explica cómo usamos datos de cuenta, perfil, mensajes y planes, medios de verificación, datos de prueba de vida, ubicación, analítica de la app, eventos de suscripción, datos de atribución y proveedores de servicios como AWS Rekognition, RevenueCat y Meta/Facebook App Events."
          ]
        },
        {
          heading: "14. Disponibilidad y Cambios del Servicio",
          paragraphs: [
            'Los Servicios se proporcionan "tal cual" y "según disponibilidad". No garantizamos que la App, sitio web, chats, planes, suscripciones, notificaciones o cualquier función sean ininterrumpidos, libres de errores, seguros, exactos o disponibles en todos los dispositivos.',
            "Podemos probar, modificar, suspender, discontinuar, restringir o eliminar funciones, planes, ofertas, contenido o acceso en cualquier momento. También podemos realizar mantenimiento o actualizaciones que afecten temporalmente la disponibilidad."
          ]
        },
        {
          heading: "15. Exenciones y Limitación de Responsabilidad",
          paragraphs: [
            "En la máxima medida permitida por la ley, Viste Ltd no es responsable de la conducta de usuarias, servicios de terceros, planes cancelados o no disponibles, problemas de locales, problemas de transporte, disputas de pago entre usuarias, información de planes inexacta, oportunidades perdidas, pérdidas indirectas, pérdida de beneficios, pérdida de datos, angustia emocional, insatisfacción personal, daño reputacional o daños consecuentes.",
            "La orientación de seguridad, funciones de verificación, herramientas de moderación, herramientas de reporte, recomendaciones y normas de la comunidad se proporcionan para apoyar los Servicios, pero no crean garantía, obligación de monitorizar, obligación de rescate, obligación de investigar, deber fiduciario ni relación especial entre tú y Chisme.",
            "Nada en estos Términos excluye o limita responsabilidad que no pueda excluirse bajo la ley aplicable, incluida la responsabilidad por muerte o lesiones personales causadas por negligencia, fraude, tergiversación fraudulenta o derechos obligatorios de consumo.",
            "Si somos responsables ante ti por cualquier motivo, nuestra responsabilidad total se limitará al mayor importe entre lo que pagaste a Chisme en los 12 meses anteriores al evento que dio lugar a la reclamación o GBP 100, salvo que la ley aplicable exija otra cosa."
          ]
        },
        {
          heading: "16. Indemnidad",
          paragraphs: [
            "Aceptas indemnizar y mantener indemne a Viste Ltd, sus afiliadas, sucesores, cesionarios, administradores, directivos, empleados, contratistas, agentes, licenciantes, proveedores de servicios y proveedores de pago frente a reclamaciones, pérdidas, responsabilidades, daños, costes y gastos derivados de tu Contenido, tu uso o uso indebido de los Servicios, tu participación en planes o interacciones presenciales, tu incumplimiento de estos Términos, la infracción de derechos de otra persona, tu disputa con otra usuaria o tercero, o tu conducta ilegal, salvo cuando lo prohíba la ley aplicable."
          ]
        },
        {
          heading: "17. Terminación",
          paragraphs: [
            "Puedes dejar de usar Chisme o eliminar tu cuenta en cualquier momento. La eliminación de la cuenta no cancela automáticamente suscripciones de tiendas de apps.",
            "Podemos suspender, limitar o terminar el acceso a los Servicios en cualquier momento cuando creamos que has infringido estos Términos, creado riesgo para otras usuarias, usado indebidamente los Servicios, fallado la verificación, infringido derechos o causado riesgo legal u operativo.",
            "Tras la terminación, tu derecho a usar los Servicios finaliza inmediatamente. Las disposiciones que por su naturaleza deban sobrevivir, incluidas titularidad, licencias, exenciones, límites de responsabilidad, indemnidad y resolución de disputas, seguirán aplicándose."
          ]
        },
        {
          heading: "18. Acuerdo de Arbitraje y Renuncia a Acciones Colectivas para Estados Unidos",
          paragraphs: [
            "Esta sección se aplica solo a usuarias ubicadas en Estados Unidos, salvo cuando lo prohíba la ley aplicable. Tú y Viste Ltd aceptáis que cualquier disputa, reclamación o controversia derivada de los Servicios o estos Términos, o relacionada con ellos, se resolverá mediante arbitraje individual vinculante conforme a la Federal Arbitration Act, en lugar de ante un tribunal, excepto que cualquiera de las partes pueda presentar una reclamación individual ante un tribunal de menor cuantía cuando esté permitido o solicitar medidas cautelares o equitativas por uso indebido de propiedad intelectual o acceso no autorizado.",
            "Tú y Viste Ltd renunciáis al derecho a juicio con jurado y aceptáis que las disputas solo podrán presentarse a título individual, no como demandante, reclamante o miembro de una acción colectiva, consolidada, de representante privado o procedimiento representativo. El árbitro podrá conceder reparación solo a la parte individual que la solicite y únicamente en la medida necesaria para resolver su reclamación individual.",
            "Antes de iniciar un arbitraje, la parte que presente una reclamación debe enviar una notificación escrita que describa la disputa y la reparación solicitada a legal@chisme.social. Las partes intentarán resolver la disputa de forma informal durante 30 días. Si la disputa no se resuelve, cualquiera de las partes podrá iniciar arbitraje con un proveedor de arbitraje mutuamente acordado o, si las partes no llegan a un acuerdo, con un proveedor seleccionado por un tribunal con jurisdicción.",
            "Si alguna parte de este acuerdo de arbitraje o renuncia a acciones colectivas se considera inaplicable respecto de una reclamación o remedio concreto, dicha parte se separará solo en la medida necesaria y el resto seguirá vigente en la máxima medida permitida por la ley."
          ]
        },
        {
          heading: "19. Ley Aplicable y Resolución de Disputas",
          paragraphs: [
            "Estos Términos se rigen por las leyes de Inglaterra y Gales. Los tribunales de Inglaterra y Gales tendrán jurisdicción, salvo cuando las leyes obligatorias de consumo te den derecho a presentar reclamaciones en tu país de residencia.",
            "Las usuarias en la UE, Reino Unido y otras jurisdicciones pueden beneficiarse de protecciones obligatorias de consumo de su país, estado o lugar de residencia. Nada en estos Términos limita derechos que legalmente no puedan limitarse. Las disputas pueden resolverse a través de la plataforma de Resolución de Litigios en Línea de la UE cuando sea aplicable: {{odr}}."
          ]
        },
        {
          heading: "20. Cambios, Acuerdo Completo y Contacto",
          paragraphs: [
            "Podemos actualizar estos Términos de vez en cuando. Actualizaremos la fecha de Última actualización y, cuando corresponda, notificaremos a las usuarias a través de la App, sitio web, email u otros medios razonables. El uso continuado de los Servicios tras una actualización significa que aceptas los Términos actualizados.",
            "Estos Términos, junto con la {{privacy}}, las {{guidelines}}, los {{safety}} y cualquier término específico de función que se te muestre, forman el acuerdo completo entre tú y Viste Ltd respecto de los Servicios.",
            "Si alguna parte de estos Términos se considera inválida o inaplicable, las partes restantes seguirán vigentes. El hecho de que no hagamos cumplir un término no supone una renuncia a nuestro derecho a hacerlo más adelante. Contacto: {{legal}}."
          ]
        }
      ]
    },

    // Privacy Policy
    privacy: {
      backHome: "← Volver al inicio",
      title: "Política de Privacidad",
      seoTitle: "Política de Privacidad | Chisme",
      seoDescription:
        "Descubre cómo Chisme recopila, usa y protege tus datos personales.",
      lastUpdated: "Última actualización:",
      lastUpdatedDate: "06.07.2026",
      section1: {
        heading: "1. Introducción",
        para1: 'Chisme ("nosotras", "nuestro" o "nos") respeta tu privacidad y se compromete a proteger tus datos personales. Esta Política de Privacidad explica cómo recopilamos, usamos, compartimos y protegemos tu información cuando usas la aplicación móvil Chisme (la "App") y servicios relacionados.',
        para2: "Al usar Chisme, reconoces esta Política de Privacidad. Cuando la ley exige consentimiento, como para acceso opcional a ubicación, marketing, identificadores de seguimiento publicitario o verificación facial, nos basamos en la acción afirmativa que realizas en el flujo correspondiente o solicitamos el consentimiento por separado."
      },
      section2: {
        heading: "2. Controlador de Datos",
        para1: 'El controlador de datos es "Viste Ltd", registrada en el Reino Unido con número de empresa "13232923", con domicilio registrado en 3-7, Temple Avenue, Suite 259 Temple Chambers, Londres, Inglaterra, EC4Y 0DA.',
        contact: "Contacto: ",
        para2: "Para usuarias de la UE/EEE (incluida España), cumplimos con el GDPR de la UE y, si es necesario, designaremos un representante en la UE."
      },
      section3: {
        heading: "3. Información que Recopilamos",
        subsectionA: {
          heading: "(a) Información que proporcionas",
          items: [
            "Detalles de registro (nombre, email, fecha de nacimiento, identidad de género).",
            "Detalles del perfil (fotos, intereses, preferencias de ubicación).",
            "Contenido que compartes (mensajes, eventos, publicaciones)."
          ]
        },
        subsectionB: {
          heading: "(b) Información que recopilamos automáticamente",
          items: [
            "Datos del dispositivo (modelo, sistema operativo, identificadores únicos).",
            "Datos de uso (interacciones en la App, funciones usadas).",
            "Ubicación aproximada (si la habilitas para el descubrimiento de eventos)."
          ]
        },
        subsectionC: {
          heading: "(c) Información de terceros",
          items: [
            "Datos de pagos de App Store / Google Play.",
            "Cuentas de redes sociales (si eliges vincularlas)."
          ]
        },
        subsectionD: {
          heading: "(d) Fotos, Videos y Pruebas de Vida de Verificación",
          para1: 'Para ayudar a garantizar la seguridad y autenticidad de nuestra comunidad, requerimos que las usuarias completen la verificación de cuenta, lo que puede incluir enviar una selfie o foto, grabar un video corto de prueba de vida, completar acciones sencillas de prueba de vida o permitir que se extraigan fotogramas de un video de verificación (en conjunto, "Medios de Verificación"). Los Medios de Verificación pueden incluir el rostro de la usuaria y pueden compararse con sus propias fotos de perfil mediante AWS Rekognition, un servicio proporcionado por Amazon Web Services, Inc.',
          para2: "Esta comparación uno a uno y el proceso de prueba de vida pueden implicar el procesamiento de imágenes faciales, información de comparación facial, acciones de prueba de vida y resultados de verificación relacionados, que pueden considerarse datos biométricos o identificadores biométricos según ciertas leyes."
        },
        note: "No recopilamos a sabiendas datos de personas menores de 18 años."
      },
      section4: {
        heading: "4. Cómo Usamos tu Información",
        items: [
          "Para proporcionar y operar la App.",
          "Para crear y gestionar tu cuenta.",
          "Para conectarte con otras usuarias y eventos.",
          "Para garantizar la seguridad y el cumplimiento de nuestra política centrada en mujeres.",
          "Para procesar suscripciones y pagos.",
          "Para enviar actualizaciones, notificaciones y respuestas de soporte al cliente.",
          "Para mejorar nuestros servicios y personalizar tu experiencia.",
          "Para cumplir con obligaciones legales.",
          "Para verificar que las usuarias son personas reales y prevenir cuentas falsas o fraudulentas.",
          "Para analizar fotos e imágenes subidas mediante AWS Rekognition con fines de seguridad y cumplimiento de nuestras políticas, incluida la detección de contenido explícito, violento u otro contenido prohibido.",
          "Para medir la eficacia de nuestras campañas de marketing y entender las conversiones de suscripción.",
          "Para atribuir instalaciones de la app, aperturas, pruebas y eventos de suscripción a campañas publicitarias, incluido mediante herramientas de medición agregadas y que preservan la privacidad."
        ]
      },
      section4a: {
        heading: "4a. Datos de Ubicación",
        para1: "Con tu consentimiento, Chisme accede a la ubicación de tu dispositivo cuando abres la app. Esta información se usa únicamente para:",
        items: [
          "Mostrar tu posición en el mapa dentro de la App.",
          "Mostrar usuarias y planes ubicados cerca de ti."
        ],
        para2: "Tu ubicación se actualiza solo cuando abres la app y no se rastrea en tiempo real. No compartimos tus datos de ubicación con terceros. Puedes retirar el consentimiento en cualquier momento deshabilitando los permisos de ubicación en la configuración de tu dispositivo, pero ciertas funciones pueden no funcionar correctamente sin ella."
      },
      section4b: {
        heading: "4b. Medios de Verificación, Pruebas de Vida y Datos Faciales",
        para1: "Los Medios de Verificación se utilizan únicamente para verificar que una cuenta pertenece a una persona real, completar pruebas de vida y apoyar la seguridad, confianza y prevención de fraudes en la plataforma. Cuando eliges iniciar la verificación y enviar Medios de Verificación, tu imagen facial, datos de comparación facial, acciones de prueba de vida y resultados de verificación relacionados pueden recopilarse y procesarse para verificación de identidad y seguridad de la plataforma según se describe en esta Política.",
        notUsedForIntro: "No utilizamos los Medios de Verificación para:",
        notUsedForItems: [
          "Buscar tu rostro entre otras usuarias, crear una base de datos facial consultable o identificarte en el mundo real",
          "Vender, alquilar, intercambiar o beneficiarnos de otro modo de datos biométricos o identificadores biométricos",
          "Publicidad, perfilado o seguimiento",
          "Entrenamiento de modelos de inteligencia artificial o aprendizaje automático"
        ],
        para2: "Los Medios de Verificación, las fotos de perfil usadas para la comparación, los datos de prueba de vida y cualquier resultado o dato de comparación facial se acceden solo para propósitos de verificación, moderación y seguridad. Los Medios de Verificación, los datos de prueba de vida y los datos de comparación facial se eliminan dentro de los 7 días posteriores a completar la verificación, salvo que debamos conservarlos más tiempo para cumplir con la ley o investigar fraudes o incidentes de seguridad."
      },
      section5: {
        heading: "5. Base Legal (GDPR / GDPR del Reino Unido)",
        items: [
          "Contrato – para proporcionar los servicios que solicitas.",
          "Consentimiento – para funciones opcionales como emails de marketing o datos de ubicación.",
          "Consentimiento u otra base legal aplicable – para la verificación facial y pruebas de vida que impliquen imágenes faciales, datos de comparación facial, datos de prueba de vida o resultados de verificación relacionados que puedan considerarse datos biométricos según las leyes aplicables.",
          "Consentimiento – para identificadores de seguimiento publicitario y otro seguimiento opcional cuando lo exijan la ley o las normas de la plataforma.",
          "Obligación legal – para el cumplimiento de la ley aplicable (impuestos, consumo, seguridad).",
          "Intereses legítimos – para garantizar la seguridad, prevenir fraudes, moderar fotos subidas y otro contenido, y mejorar servicios.",
          "Intereses legítimos – para medir el rendimiento de la app, entender las conversiones de suscripción y mejorar nuestro marketing, salvo que se requiera consentimiento."
        ]
      },
      section6: {
        heading: "6. Compartir tu Información",
        intro: "Podemos compartir tus datos con:",
        items: [
          "Proveedores de servicios (hosting, analítica, procesadores de pagos).",
          "Autoridades legales si lo requiere la ley.",
          "Otras usuarias, pero solo con los datos que elijas compartir en tu perfil."
        ],
        attributionParas: [
          "Utilizamos proveedores externos de analítica, gestión de suscripciones y atribución publicitaria, incluidos RevenueCat y Meta/Facebook App Events, para operar suscripciones, entender el rendimiento de la app y medir la eficacia de nuestras campañas publicitarias.",
          "Cuando interactúas con la App o compras una suscripción, podemos compartir datos limitados de eventos de la app, como aperturas, inicios de prueba, compras de suscripción, identificadores de producto, valor de compra, moneda, información del dispositivo e identificadores de cuenta con estos proveedores. Cuando sea compatible, cierta información de contacto como el email o el nombre puede hashearse o procesarse de otro modo antes de usarse para emparejamiento o atribución.",
          "Utilizamos AWS Rekognition (Amazon Web Services, Inc.) como proveedor de servicios/encargado del tratamiento para moderación de imágenes mediante IA y verificación de identidad. Las fotos e imágenes subidas pueden analizarse automáticamente para detectar contenido explícito, violento u otro contenido prohibido, y las imágenes marcadas pueden ser revisadas por moderadoras humanas autorizadas bajo obligaciones de confidencialidad.",
          "En iOS, solicitamos permiso de App Tracking Transparency antes de acceder a identificadores de seguimiento publicitario como el IDFA. Si deniegas el permiso de seguimiento, no usamos el IDFA para seguimiento publicitario, pero podemos seguir enviando eventos de la app para medición agregada cuando esté permitido."
        ],
        note: "No vendemos tus datos personales.",
        verificationNote: "Los Medios de Verificación, los datos de prueba de vida y los datos de comparación facial no se comparten con terceros para marketing o publicidad. AWS Rekognition procesa estos datos solo como nuestro proveedor de servicios/encargado del tratamiento para verificación y moderación de imágenes, y el acceso se limita a esos propósitos y está sujeto a obligaciones contractuales de confidencialidad y protección de datos."
      },
      section7: {
        heading: "7. Transferencias Internacionales",
        text: "Tus datos pueden ser transferidos fuera del Reino Unido y la UE/EEE. Cuando esto ocurra, usamos salvaguardas como Cláusulas Contractuales Estándar (SCC) para garantizar una protección adecuada."
      },
      section8: {
        heading: "8. Retención de Datos",
        items: [
          "Conservamos tus datos mientras tengas una cuenta.",
          "Si eliminas tu cuenta, borramos o anonimizamos tus datos dentro de 30 días, a menos que debamos conservar algunos registros por obligaciones legales (p. ej., registros fiscales).",
          "Podemos conservar datos de cuenta, reportes, mensajes, contenido, información del dispositivo, logs y otros registros cuando sea razonablemente necesario por seguridad, prevención de fraude, resolución de disputas, reclamaciones legales, solicitudes de autoridades, aplicación de nuestros Términos o protección de usuarias y de los Servicios."
        ],
        verificationNote: "Los Medios de Verificación, los datos de prueba de vida y los resultados o datos de comparación facial se eliminan dentro de los 7 días posteriores a completar la verificación, salvo que la conservación sea necesaria para cumplir con la ley o investigar fraudes o incidentes de seguridad. Cuando sea posible, minimizamos o eliminamos el contenido de imagen o video subyacente cuando ya no sea necesario para fines de seguridad o legales."
      },
      section9: {
        heading: "9. Tus Derechos (GDPR / GDPR del Reino Unido)",
        intro: "Tienes derecho a:",
        items: [
          "Acceder a tus datos.",
          "Rectificar datos incorrectos.",
          'Solicitar eliminación ("derecho al olvido").',
          "Restringir el procesamiento.",
          "Oponerse al procesamiento.",
          "Portabilidad de datos.",
          "Retirar el consentimiento en cualquier momento, incluido el consentimiento para la verificación facial cuando el consentimiento sea la base legal aplicable, cambiando la configuración de tu cuenta cuando esté disponible o contactándonos.",
          "Presentar una queja ante tu autoridad de datos local (ICO en el Reino Unido, AEPD en España)."
        ],
        requestsText: "Las solicitudes pueden hacerse en "
      },
      section10: {
        heading: "10. Seguridad",
        text: "Usamos medidas estándar de la industria (encriptación, firewalls, controles de acceso) para proteger tus datos. Sin embargo, ningún sistema es 100% seguro. Las usuarias son responsables de proteger sus detalles de inicio de sesión.",
        verificationNote: "Los Medios de Verificación, imágenes faciales, datos de prueba de vida y datos de comparación facial están protegidos usando las mismas medidas de seguridad estándar de la industria aplicadas a otros datos personales sensibles, y el acceso se restringe a personal autorizado y proveedores de servicios que lo necesiten para verificación, moderación, seguridad, fines legales o incidentes de seguridad."
      },
      section11: {
        heading: "11. Privacidad de Menores",
        text: "La App no está destinada a usuarias menores de 18 años. Eliminaremos cualquier cuenta sospechosa de uso por menores."
      },
      section12: {
        heading: "12. Cambios en Esta Política",
        text: 'Podemos actualizar esta Política de Privacidad. Las actualizaciones se publicarán en la App con una nueva fecha de "Última actualización".'
      },
      section13: {
        heading: "13. Contáctanos",
        intro: "Si tienes preguntas o inquietudes:",
        email: "Email: ",
        address: "Dirección: 3-7, Temple Avenue, Suite 259 Temple Chambers, Londres, Inglaterra, EC4Y 0DA"
      }
    },

    // Community Guidelines
    guidelines: {
      backHome: "← Volver al inicio",
      title: "Directrices de la Comunidad",
      seoTitle: "Directrices de la Comunidad | Chisme",
      seoDescription:
        "Normas de la comunidad para mantener un espacio seguro y respetuoso en Chisme.",
      lastUpdated: "Última actualización:",
      lastUpdatedDate: "06.07.2026",
      intro: "Chisme es una comunidad centrada en mujeres para hacer amigas, conversar y unirse a planes en la vida real en un entorno seguro y respetuoso. Estas Normas de la Comunidad forman parte de nuestros Términos y se aplican en la App, en chats, perfiles, planes y encuentros presenciales. Si determinamos que has infringido estas Normas, podemos eliminar contenido, advertirte, restringir funciones, suspender tu cuenta o eliminarte permanentemente de Chisme, según la gravedad y el historial de la conducta.",
      section1: {
        heading: "1. Normas de la Comunidad",
        items: [
          "Chisme no es una app de citas, una app de ligues ni una plataforma para solicitación sexual. No se permite mensajería sexual inapropiada, presión, acoso o uso indebido de las funciones de mensajería.",
          "Trata a cada miembro con amabilidad, respeto y honestidad. Están prohibidos el acoso, bullying, intimidación, amenazas, humillación o lenguaje degradante.",
          "Sé auténtica. No crees perfiles falsos, no suplantes a otras personas ni tergiverses tu edad, identidad de género, ubicación, relación con la comunidad o intenciones.",
          "Usa Chisme para amistad genuina, planes en grupo y comunidad. Los mensajes repetidos de bajo esfuerzo, irrelevantes o masivos no solicitados pueden provocar restricciones o eliminación."
        ]
      },
      section2: {
        heading: "2. Contenido y Conducta Prohibidos",
        items: [
          "No se permite discurso de odio, discriminación o contenido deshumanizante por raza, etnia, nacionalidad, género, orientación sexual, religión, discapacidad, edad o cualquier característica protegida.",
          "No se permite violencia, amenazas, stalking, coacción, conducta física indebida, conducta sexual indebida ni contenido que promueva autolesiones, violencia, actividad ilegal o conductas peligrosas.",
          "No compartas contenido explícito, vulgar, sexualmente provocativo, explotador o sexual no solicitado. Los planes no deben incluir trabajo sexual, pornografía ni su promoción.",
          "No uses Chisme para fraude, estafas, solicitudes de dinero, beneficio económico engañoso, spam, enlaces engañosos, promociones no autorizadas, scraping, bots, evasión de bloqueos o intentos de eludir nuestros sistemas.",
          "Las usuarias deben tener al menos 18 años. No uses Chisme para compartir fotos o información de menores ni para organizar contacto con personas menores de 18."
        ]
      },
      section3: {
        heading: "3. Perfiles y Fotos",
        items: [
          "El nombre de tu perfil debe reflejar tu nombre real o un apodo real, no la identidad de otra persona ni una identidad engañosa.",
          "Las fotos de perfil y los medios de verificación deben ser tuyos y no deben suplantar ni tergiversar a otra persona.",
          "No incluyas información sensible como números de teléfono, direcciones, datos financieros, emails, detalles privados de viaje u otros datos personales en tu biografía.",
          "Mantén el contenido del perfil apropiado para una app de amistad y comunidad. El contenido explícito, engañoso, de odio, comercial o inseguro puede eliminarse."
        ]
      },
      section4: {
        heading: "4. Chats y Privacidad",
        items: [
          "Protege tu privacidad y la de otras personas. No compartas ni solicites públicamente teléfonos, direcciones, emails, información financiera o ubicaciones privadas exactas.",
          "Mantén las conversaciones y la coordinación de planes dentro de Chisme salvo que todas las personas involucradas elijan libremente otra cosa. No presiones a usuarias para pasar a otro canal.",
          "No hagas capturas, grabes, publiques ni compartas chats, fotos, perfiles, ubicaciones o información personal fuera de Chisme sin consentimiento.",
          "Respeta los límites. Si alguien dice que no, deja de responder, te bloquea o pide espacio, no sigas contactando ni presionando."
        ]
      },
      section5: {
        heading: "5. Planes y Encuentros Presenciales",
        items: [
          "Los planes son para usuarias de Chisme mayores de 18 años y deben cumplir las leyes locales, normas del lugar y expectativas de seguridad, incluidas las reglas sobre alcohol, sustancias controladas y alteración del orden público.",
          "Elige espacios públicos, informa a una amiga o familiar de dónde vas, revisa cuidadosamente los detalles del plan y confía en tu intuición si algo parece inseguro.",
          "Las creadoras deben proporcionar detalles precisos y oportunos como hora, ubicación, límites de participantes, costes y consideraciones de accesibilidad. Las participantes deben verificar los detalles antes de asistir.",
          "Sé fiable y respetuosa con la asistencia. Si necesitas cancelar, avisa al grupo pronto. Cancelaciones de última hora o ausencias repetidas pueden dar lugar a acciones sobre la cuenta.",
          "Las participantes son responsables de su propio transporte, seguridad, salud, pertenencias, cuentas y acuerdos de pago. Chisme no supervisa encuentros ni garantiza la conducta o los resultados de las usuarias."
        ]
      },
      section6: {
        heading: "6. Bloqueo, Reportes y Emergencias",
        items: [
          "Si hay una emergencia o peligro inmediato, contacta primero con los servicios locales de emergencia o las autoridades.",
          "Puedes bloquear usuarias desde su perfil o conversación cuando esté disponible. Bloquear impide el contacto, pero no reporta automáticamente la conducta a Chisme.",
          "Si alguien infringe estas Normas, reporta el perfil, contenido, chat o plan a través de la App o contáctanos para que nuestro equipo pueda revisarlo.",
          "Los reportes no se monitorizan como canal de respuesta de emergencia, y Chisme no garantiza un tiempo de respuesta, resultado, investigación o medida de cumplimiento concreta.",
          "Usar indebidamente el bloqueo o los reportes para acosar, tomar represalias, silenciar o incomodar a otras personas también puede dar lugar a acciones sobre la cuenta."
        ]
      },
      section7: {
        heading: "7. Cumplimiento y Responsabilidad",
        items: [
          "Chisme puede revisar contenido, chats, perfiles, planes y comportamientos reportados o sospechosos que afecten a la seguridad de la plataforma, incluida conducta relacionada con encuentros presenciales.",
          "Podemos eliminar contenido, limitar visibilidad, restringir mensajería o funciones de planes, exigir verificación, suspender cuentas, terminar cuentas o avisar a las autoridades cuando sea apropiado o legalmente obligatorio.",
          "Está prohibido intentar evadir medidas creando nuevas cuentas, usando información falsa, VPNs o automatización, o animando a otras personas a infringir estas Normas.",
          "Estas Normas pueden actualizarse a medida que Chisme crece. El uso continuado de Chisme significa que aceptas seguir la versión vigente."
        ]
      }
    },

    // Safety
    safety: {
      backHome: "← Volver al inicio",
      title: "Consejos de Seguridad y Etiqueta en Planes",
      seoTitle: "Consejos de Seguridad | Chisme",
      seoDescription:
        "Orientación práctica de seguridad para usar Chisme, unirse a planes y conocer nuevas personas.",
      lastUpdated: "Última actualización:",
      lastUpdatedDate: "06.07.2026",
      intro:
        "Chisme está diseñada para ayudar a mujeres a hacer amigas, conversar y unirse a planes en la vida real con mayor confianza. La seguridad es una responsabilidad compartida. Estos consejos no eliminan todos los riesgos, no crean una garantía de seguridad ni hacen a Chisme responsable de la conducta de usuarias, locales, transporte o servicios de terceros.",
      sections: [
        {
          heading: "1. Antes de Unirte a un Plan",
          items: [
            "Revisa cuidadosamente los detalles del plan, incluida la hora, ubicación, límites de participantes, costes previstos, consideraciones de accesibilidad y normas del lugar.",
            "Queda en un lugar público, bien iluminado y con otras personas alrededor, especialmente cuando conozcas a alguien por primera vez.",
            "Informa a una amiga, familiar o persona de confianza de dónde vas, con quién esperas reunirte y cuándo esperas volver.",
            "Organiza tu propio transporte de ida y vuelta para poder marcharte en cualquier momento.",
            "Mantén la comunicación relacionada con el plan dentro de Chisme hasta que te sientas cómoda. Las conversaciones en la App proporcionan contexto si necesitas reportar una incidencia.",
            "Verifica los detalles del lugar de forma independiente cuando sea necesario. La información mostrada en la App, incluidos los datos proporcionados por usuarias, puede cambiar o estar incompleta."
          ]
        },
        {
          heading: "2. Durante un Plan",
          subsections: [
            {
              heading: "Seguridad Personal",
              items: [
                "Mantente atenta a tu entorno y conserva tus pertenencias, teléfono y bebidas a la vista.",
                "Usa tu criterio con el alcohol o cualquier sustancia que pueda afectar a tu capacidad de decidir o marcharte de forma segura.",
                "Confía en tu intuición. Si algo parece inseguro o inapropiado, abandona la situación y busca ayuda.",
                "Si te sientes insegura, contacta inmediatamente con el personal del lugar, autoridades locales, servicios de emergencia o una persona de confianza."
              ]
            },
            {
              heading: "Etiqueta y Conducta",
              items: [
                "Trata a las demás participantes con respeto y mantén conversaciones inclusivas y apropiadas.",
                "Respeta los límites personales, la privacidad y el consentimiento en todo momento.",
                "Prepárate para gestionar tu propia cuenta, compras, propinas y acuerdos de pago. Chisme no gestiona pagos compartidos entre usuarias.",
                "Cumple las leyes locales, normas del lugar y expectativas culturales."
              ]
            }
          ]
        },
        {
          heading: "3. Después de un Plan",
          items: [
            "Continúa las conversaciones en Chisme hasta que te sientas cómoda compartiendo datos de contacto adicionales.",
            "Evita aceptar transporte de personas que no conoces bien, especialmente si hay alcohol u otras sustancias de por medio.",
            "Si has experimentado una conducta inapropiada, insegura o sospechosa, repórtala a través de la App lo antes posible.",
            "Si necesitas cancelar planes futuros, avisa al grupo de forma rápida y respetuosa."
          ]
        },
        {
          heading: "4. Bloqueo y Reportes",
          items: [
            "Usa el bloqueo para detener contacto no deseado cuando la función esté disponible.",
            "Bloquear no reporta automáticamente a una usuaria a Chisme. Si alguien infringe nuestras Normas de la Comunidad o genera una preocupación de seguridad, envía un reporte a través de la App.",
            "Los reportes nos ayudan a revisar conductas dañinas, aplicar nuestras normas y mejorar la seguridad de la comunidad.",
            "No uses indebidamente el bloqueo o los reportes para acosar, tomar represalias, silenciar o incomodar a otra usuaria."
          ]
        },
        {
          heading: "5. Seguridad Online",
          items: [
            "Nunca envíes dinero, información financiera, credenciales de cuenta, documentos de identidad o códigos de verificación a alguien que hayas conocido en Chisme.",
            "Ten cuidado con cualquier persona que pida dinero, ayuda para inversiones, donaciones, apoyo económico de emergencia o acuerdos de pago inusuales.",
            "Protege información sensible como tu dirección de casa o trabajo, rutina diaria, ubicación exacta en tiempo real, datos financieros e información privada de tu familia.",
            "Ten precaución si alguien intenta llevar la conversación fuera de Chisme muy rápidamente, evita una verificación razonable, te presiona emocionalmente o no respeta tus límites.",
            "Reporta comportamientos sospechosos, incluidos perfiles falsos, usuarias menores de edad, acoso, amenazas, spam, estafas y mensajes sexuales inapropiados."
          ]
        },
        {
          heading: "6. Emergencias y Riesgo Inmediato",
          items: [
            "Si hay una emergencia o peligro inmediato, contacta primero con los servicios locales de emergencia o las autoridades.",
            "Chisme no es un servicio de emergencia, línea de crisis, proveedor de seguridad, proveedor médico, proveedor de transporte ni autoridad policial. Los reportes enviados a través de la App no se monitorizan para respuesta de emergencia.",
            "Si estás en España o en otro país de la Unión Europea, el 112 es el número general de emergencias.",
            "Si estás viviendo violencia de género en España, el 016 ofrece apoyo y no aparece en la factura telefónica, aunque puede aparecer en el registro de llamadas según el dispositivo.",
            "Una vez estés a salvo, reporta el incidente en Chisme para que nuestro equipo pueda revisar posibles medidas sobre cuentas o contenido cuando corresponda."
          ]
        },
        {
          heading: "7. Recursos de Apoyo",
          items: [
            "Servicios de emergencia: contacta inmediatamente con las autoridades locales si hay peligro inmediato.",
            "Emergencias generales en España: 112.",
            "Apoyo frente a violencia de género en España: 016.",
            "Número de emergencias de la Unión Europea: 112.",
            "Para soporte no urgente de Chisme, contacta con hola@chisme.social o usa los reportes dentro de la App cuando estén disponibles."
          ]
        },
        {
          heading: "8. Papel de Chisme",
          items: [
            "Chisme proporciona herramientas de descubrimiento, chat, reportes, bloqueo, moderación y medidas sobre cuentas, pero no supervisa todos los planes ni garantiza la conducta de las usuarias.",
            "Las usuarias son responsables de sus propias decisiones, transporte, salud, seguridad, pertenencias y acuerdos de pago al unirse a planes o conocer a otras personas.",
            "Las funciones de seguridad y moderación pueden reducir riesgos, pero no pueden eliminar todos los riesgos de las interacciones online o presenciales.",
            "Chisme puede revisar, conservar, restringir o eliminar contenido y cuentas cuando corresponda, pero no tiene obligación de monitorizar todas las usuarias, chats, planes, locales, reportes o interacciones."
          ]
        }
      ]
    },

    homeRedesign: homeRedesignEs,
    partners: partnersEs
  }
};

export default translations;
